import { getUID } from '@redviking/argonaut-util/src/getUID';
import { getAllAppletVariables } from '@redviking/argonaut-util/src/mes/appletUtil';
import type { NodeSpec } from './NodeSpec';
import type { NodeCfg } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { getSocketTypeForVarId } from '../socket-var-helpers';
import { defineControlOpts } from '../../controls/FlowNodeControl';

export const varWriteMultipleNodeSpec: NodeSpec<NodeCfg<'varWriteMultiple'>> = {
    requiredSockets: [ 'trigger' ],
    cfg: () => ({ controlValues: { vars: [] }, id: getUID(), kind: 'varWriteMultiple', disabled: false }),
    controls: nodeCfg => ({
        vars: defineControlOpts({
            cfg: {
                kind: 'button',
                useDialog: null,
                label: 'Add Variable',
            },
            on: {
                'update:value': () => {}, // no-op, this control does not use the update:value event
                'click': (_, { flowEventEmitter }) => {
                    flowEventEmitter.emit('updateNodeCfg', {
                        newNodeCfg: {
                            ...nodeCfg,
                            controlValues: {
                                ...nodeCfg.controlValues,
                                vars: [
                                    ...nodeCfg.controlValues.vars,
                                    '',
                                ],
                            },
                        },
                    });
                },
            },
            index: 1,
            value: nodeCfg.controlValues.vars,
        }),
    }),
    inputs: nodeCfg => {
        const inputs: ReturnType<NodeSpec<NodeCfg<'varWriteMultiple'>>['inputs']> = {
            trigger: { socket: 'event', label: 'Trigger', index: 0 },
        };
        for (let index = 0; index < nodeCfg.controlValues.vars.length; index++) {
            inputs[`var${index}`] = {
                controlOpts: defineControlOpts({
                    cfg: {
                        kind: 'deletableSocket',
                        childControl: defineControlOpts({
                            cfg: {
                                kind: 'selectVar',
                            },
                            on: {
                                'update:value': (selectedVariableId, { flowEventEmitter }) => {
                                    flowEventEmitter.emit('updateNodeCfg', {
                                        newNodeCfg: {
                                            ...nodeCfg,
                                            controlValues: {
                                                ...nodeCfg.controlValues,
                                                vars: nodeCfg.controlValues.vars.map((variableId, variableIndex) => variableIndex === index ? selectedVariableId : variableId),
                                            },
                                        },
                                    });
                                },
                            },
                            value: nodeCfg.controlValues.vars[index],
                            enableWithConnectedSocket: true,
                        }),
                    },
                    value: null,
                    enableWithConnectedSocket: true,
                    on: {
                        'update:value': () => {}, // no-op, this control does not use the update:value event
                        'delete': (_, { flowEventEmitter }) => {
                            flowEventEmitter.emit('removeNodeSocket', {
                                newNodeCfg: {
                                    ...nodeCfg,
                                    controlValues: {
                                        ...nodeCfg.controlValues,
                                        vars: [
                                            ...nodeCfg.controlValues.vars.slice(0, index),
                                            ...nodeCfg.controlValues.vars.slice(index + 1),
                                        ],
                                    },
                                },
                                direction: 'input',
                                removalMethod: {
                                    index,
                                    prefix: 'var',
                                    kind: 'indexedIdentifier',
                                },
                            });
                        },
                    },
                }),
                socket: getSocketTypeForVarId(nodeCfg.controlValues.vars[index]),
            };
        }
        return inputs;
    },
    kindLabel: 'Variable Write Multiple',
    nodeCategory: 'variable',
    outputs: () => ({ next: { socket: 'event', label: 'Next' } }),
    validator: (node, appletDesignVersionConfig) => {
        const errors: Record<string, string> = {};
        if (!node.controlValues.vars.length) {
            errors.vars = 'At least one variable is required';
        } else {
            const allAppletVariables = getAllAppletVariables(appletDesignVersionConfig);
            node.controlValues.vars.forEach((variableId, index) => {
                if (!allAppletVariables[variableId]) {
                    errors[`var${index}`] = 'Variable is required';
                }
            });
        }
        return Object.keys(errors).length ? errors : true;
    },
};
